import { SensorType } from '@innovyze/shared-utils';

// Mirrors Mapbox Feature.assetCategory (src/map/features/Feature.ts:98-109)
// so PropertyPane._.groupBy(items, 'type') routes facilities/alerts/incidents correctly.
const PROPERTY_PANE_CATEGORIES = new Set([
	'alert',
	'asset',
	'sensor',
	'incident',
	'spatial',
	'facility',
	'unknown',
]);

/**
 * Categories that say nothing about *what* was selected. `GeoJsonDataService.mapType`
 * (src/map/services/GeoJsonDataService.ts:143) stamps `type: 'asset'` on every non-sensor
 * feature it serves — ONU incidents included — so a stamped generic must never shadow a
 * specific signal. Mapbox never trusted that field either: `Feature.assetType`
 * (src/map/features/Feature.ts:93-102) derives the type from srcType / the id '#' prefix /
 * `sequence` and ignores `properties.type` entirely.
 */
const GENERIC_TYPES = new Set(['asset', 'unknown']);

const isGenericType = (type?: string): boolean =>
	!type || GENERIC_TYPES.has(type.trim().toLowerCase());

const LAYER_ID_CATEGORY_ALIASES: Record<string, string> = {
	incidents: 'incident',
};

const resolveCategoryAlias = (type?: string): string | undefined => {
	const normalized = type?.trim().toLowerCase();
	if (!normalized) return type;
	return LAYER_ID_CATEGORY_ALIASES[normalized] ?? type;
};

export const assetTypeToCategory = (assetType?: string): string => {
	const normalized = assetType?.trim().toLowerCase();
	if (!normalized) return 'asset';
	if (PROPERTY_PANE_CATEGORIES.has(normalized)) return normalized;
	if (normalized === 'amspatial') return 'spatial';
	return 'asset';
};

/**
 * Mirrors the srcType handling in Mapbox `Feature.assetType`
 * (src/map/features/Feature.ts:93-97): a sensor carries a specific SensorType
 * value as its srcType (e.g. "Flow Rate") which must collapse to the generic
 * "sensor" asset type; any other srcType (e.g. "facility") *is* the asset type.
 */
export const srcTypeToAssetType = (srcType?: string): string | undefined => {
	if (!srcType) return undefined;
	if ((Object.values(SensorType) as string[]).includes(srcType)) {
		return 'sensor';
	}
	return srcType;
};

/**
 * Resolves the asset type for a selected asset, following the same precedence as
 * Mapbox `Feature.assetType` (src/map/features/Feature.ts:93-102):
 *
 *   srcType → the id's '#' prefix → `sequence` means incident → …
 *
 * with two Esri-only additions: an explicit `assetType` (the hit-test reads one
 * off the graphic) wins outright, and the layer-level asset type is consulted
 * before giving up, which is what lets id-only selections — controlled
 * `selectedAssets` prop / URL deep-links, a bare id + layerId — resolve at all.
 *
 * Generic values ('asset'/'unknown') are demoted to a last resort so a blanket
 * stamp cannot shadow a specific signal. Two bugs came out of that shadowing:
 * facilities/sensors landing in the generic "asset" category ("Facilities [0]",
 * I360I-1399), and ONU incidents doing the same (I360P-1354) because they carry
 * no srcType and `GeoJsonDataService` stamps them `type: 'asset'`.
 */
export const resolveSelectionAssetType = (input: {
	assetType?: string;
	srcType?: string;
	featureType?: string;
	/** Asset type encoded in the id itself, e.g. 'customer' in 'customer#987789'. */
	idPrefix?: string;
	/** Incident-only property. Truthiness test mirrors Feature.ts:101 exactly. */
	sequence?: unknown;
	layerAssetType?: string;
}): string | undefined => {
	const assetType = resolveCategoryAlias(input.assetType);
	const idPrefix = resolveCategoryAlias(input.idPrefix);

	const specific = [
		isGenericType(assetType) ? undefined : assetType,
		srcTypeToAssetType(input.srcType),
		isGenericType(idPrefix) ? undefined : idPrefix,
		input.sequence ? 'incident' : undefined,
		isGenericType(input.featureType) ? undefined : input.featureType,
		input.layerAssetType,
	].find(Boolean);

	// Nothing specific: keep the generic stamp so consumers still get *a* type.
	return specific || assetType || input.featureType || undefined;
};

export const normalizeSelectionType = (
	upstreamType?: string,
	assetType?: string,
): string => {
	const resolvedCategory = assetTypeToCategory(assetType);

	// Prefer explicit spatial asset type over generic upstream labels such as
	// "asset" that can appear in some Esri hit-test payloads.
	if (resolvedCategory === 'spatial') return 'spatial';

	const normalizedType = upstreamType?.trim().toLowerCase();

	// Same rule, generalised: a resolved specific category always beats a generic
	// upstream label. Without this an incident resolved via `sequence` still routed
	// to the Assets tab, because the feature's stamped `type: 'asset'` is itself a
	// valid PropertyPane category and won the check below (I360P-1354).
	if (!isGenericType(resolvedCategory) && isGenericType(normalizedType)) {
		return resolvedCategory;
	}

	if (normalizedType && PROPERTY_PANE_CATEGORIES.has(normalizedType)) {
		return normalizedType;
	}
	if (normalizedType === 'amspatial') return 'spatial';
	return assetTypeToCategory(assetType ?? upstreamType);
};

/**
 * Resolves both the `assetType` and the PropertyPane `type` (group) for a
 * selected asset in one shot, from the feature's authoritative inputs only.
 *
 * Crucially, the pane `type` is derived from the feature's own srcType/type
 * and the resolved assetType — never from a previously-computed `type`.
 * `enrichSelectedAsset` re-runs on already-enriched assets (once layers finish
 * loading); feeding its own prior output back in let a stale generic 'asset'
 * from the first pass shadow the now-resolved specific type, stranding id-only
 * facility/sensor selections in the wrong pane tab (I360I-1399).
 */
export const resolveSelectionType = (input: {
	assetType?: string;
	srcType?: string;
	featureType?: string;
	idPrefix?: string;
	sequence?: unknown;
	layerAssetType?: string;
}): { assetType: string | undefined; type: string } => {
	const assetType = resolveSelectionAssetType(input);
	const type = normalizeSelectionType(
		input.srcType ?? input.featureType,
		assetType,
	);
	return { assetType, type };
};
